import { useCallback } from "react";
import { ErrorInfo } from "../errorInfo";
import { postInternal } from "./fetch";
import { BaseContextType } from "..";

type LoginErrorInfo = ErrorInfo<"email"> | ErrorInfo<"password">;

// 失敗ログインの上限の「初回表示用ブートストラップ値」。
// 実際の上限・残り回数・ロック秒数の権威はすべてサーバ(login.py)で、レスポンスの
// max_attempts / remaining_attempts / reset_in で受け取る。この定数は最初のサーバ応答が
// 返るまでの初回描画に使うだけで、応答が来れば maxAttempts はサーバ値で上書きされる。
// (旧 DEFAULT_LOCK_SECONDS=900 はサーバ窓の二重管理だったため廃止。ロック秒数は常に
//  サーバの reset_in を使う — バックストップ 429 も app.py が reset_in を返すようにした。)
export const MAX_LOGIN_ATTEMPTS = 5;

// ログイン結果。残り試行回数・上限・ロック情報はサーバ(権威)から受け取る。
// これによりブラウザをリフレッシュしても回数は回復しない。
export type LoginOutcome = {
  errors: LoginErrorInfo[];
  // サーバが返した残り試行回数。null = サーバに問い合わせていない(入力バリデーション/通信エラー)
  remainingAttempts: number | null;
  // サーバが返した上限値(max_attempts)。null = 取得できなかった(表示はブートストラップ値を維持)
  maxAttempts: number | null;
  // 失敗カウンタがリセットされるまでの秒数。残り 0 のときはロック解除までの待ち時間。
  // 表示の永続化(更新後もロック・残り回数を維持)とロックのカウントダウンに使う。
  resetIn: number;
  locked: boolean;
};

type LoginResponse = {
  message?: string;
  status?: string;
  token?: string;
  admin_token?: string;
  remaining_attempts?: number;
  max_attempts?: number;
  reset_in?: number;
};

export const useUserLogin = (setSession: BaseContextType) => {
  const userLogin = useCallback(
    async (email: string, password: string): Promise<LoginOutcome> => {
      const errInfo = validate(email, password);
      if (errInfo.length > 0) {
        // サーバへは送らない → 試行回数は消費させない
        return notSubmitted(errInfo);
      }

      let res: Response;
      try {
        // 共有 post() は 429 で例外を投げボディを捨てるため、ロック情報を読めるよう低レベル fetch を使う。
        res = await postInternal(`/api/login/user`, {
          body: JSON.stringify({
            login_email: email,
            login_password: password,
          }),
        });
      } catch (e) {
        console.error(e);
        return networkError();
      }

      const data = await safeJson(res);

      if (res.ok && data.status === "lu1" && data.token) {
        localStorage.setItem("token", data.token);
        setSession({ user: data.token });
        return success();
      }

      return failureOutcome(res, data);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSession]
  );

  return userLogin;
};

export const useAdminLogin = (setSession: BaseContextType) => {
  const adminLogin = useCallback(
    async (email: string, password: string): Promise<LoginOutcome> => {
      const errInfo = validate(email, password);
      if (errInfo.length > 0) {
        return notSubmitted(errInfo);
      }

      let res: Response;
      try {
        res = await postInternal(`/api/login/admin`, {
          body: JSON.stringify({
            login_admin_email: email,
            login_admin_password: password,
          }),
        });
      } catch (e) {
        console.error(e);
        return networkError();
      }

      const data = await safeJson(res);

      if (res.ok && data.status === "la1" && data.admin_token) {
        localStorage.setItem("admin_token", data.admin_token);
        setSession({ admin: data.admin_token });
        return success();
      }

      return failureOutcome(res, data);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSession]
  );

  return adminLogin;
};

const safeJson = async (res: Response): Promise<LoginResponse> => {
  try {
    return (await res.json()) as LoginResponse;
  } catch {
    return {};
  }
};

const toNumber = (value: unknown): number | null =>
  typeof value === "number" ? value : null;

const success = (): LoginOutcome => ({
  errors: [],
  remainingAttempts: MAX_LOGIN_ATTEMPTS,
  maxAttempts: MAX_LOGIN_ATTEMPTS,
  resetIn: 0,
  locked: false,
});

const notSubmitted = (errors: LoginErrorInfo[]): LoginOutcome => ({
  errors,
  remainingAttempts: null,
  maxAttempts: null,
  resetIn: 0,
  locked: false,
});

const networkError = (): LoginOutcome => ({
  errors: [
    {
      type: "password",
      message: "通信エラーが発生しました。時間をおいて再度お試しください。",
    },
  ],
  remainingAttempts: null,
  maxAttempts: null,
  resetIn: 0,
  locked: false,
});

const failureOutcome = (res: Response, data: LoginResponse): LoginOutcome => {
  const remaining = toNumber(data.remaining_attempts);
  const maxAttempts = toNumber(data.max_attempts);
  // ロック秒数は常にサーバの reset_in を使う(自前カウンタの上限到達も、バックストップ 429 も
  // サーバが reset_in を返す)。固定フォールバック(旧 DEFAULT_LOCK_SECONDS)は廃止。
  const resetIn = toNumber(data.reset_in) ?? 0;

  // ロック(自前カウンタの上限到達 or バックストップのレート制限 429)
  if (res.status === 429 || data.status === "locked") {
    return {
      errors: [],
      remainingAttempts: 0,
      maxAttempts,
      resetIn,
      locked: true,
    };
  }

  // 認証失敗(lu2/lu3, la2/la3)
  return {
    errors: [
      {
        type: "password",
        message: "パスワードが間違っています",
      },
    ],
    remainingAttempts: remaining,
    maxAttempts,
    resetIn,
    locked: remaining !== null && remaining <= 0,
  };
};

const validate = (email: string, password: string) => {
  const errInfo: LoginErrorInfo[] = [];

  if (email === "") {
    errInfo.push({
      type: "email",
      message: "メールアドレスを入力してください",
    });
  }
  if (password === "") {
    errInfo.push({
      type: "password",
      message: "パスワードを入力してください",
    });
  }
  return errInfo;
};
